// A listagem devolve is_pending como inteiro, mas as respostas de salvar,
// arquivar e desarquivar vem direto do Eloquent, onde um tinyint pode chegar
// como a string "0". Nesse caso `!item.is_pending` era falso e `is_pending === 0`
// tambem, entao o card sumia de todas as colunas — ou pior, pulava para
// "Pendentes do CRM", ja que "0" e truthy. As tres funcoes abaixo isolam isso.
const isPending = (item) => Number(item.is_pending) === 1;

const isArchived = (item) => (
    item.archived_at !== null &&
    item.archived_at !== undefined &&
    item.archived_at !== '0000-00-00 00:00:00'
);

export function checkIfIsNotStarted(item) {
  return (
      item.processed_at === null &&
      item.finalized_at === null &&
      !isPending(item) &&
      !isArchived(item)
  );
}

export function checkIfInProcess(item) {
  return (
      item.processed_at !== null &&
      item.finalized_at === null &&
      !isPending(item) &&
      !isArchived(item)
  );
}

export function checkIfIsDone(item) {
  return (
      item.finalized_at !== null &&
      !isPending(item) &&
      !isArchived(item)
  );
}

export function checkIfIsPending(item) {
  return isPending(item) && !isArchived(item);
}

export function checkIfIsArchived(item) {
  return isArchived(item) && !isPending(item);
}

// Fonte unica das categorias do societario. Antes cada tela mantinha a propria
// copia deste mapa e elas divergiam: o drawer de pendentes pintava a Troca de
// Contabilidade de vermelho e nao conhecia a categoria 7, que saia sem rotulo.
export const CATEGORIES = {
  1: { color: '#52c41a', short: 'Abertura', full: 'Abertura de Empresa' },
  2: { color: '#faad14', short: 'Alteração', full: 'Alteração Contratual' },
  3: { color: '#595959', short: 'Encerramento', full: 'Encerramento de Empresa' },
  4: { color: '#1890ff', short: 'Tarefa', full: 'Tarefa esporádica' },
  5: { color: '#388E3C', short: 'Troca de Contabilidade', full: 'Troca de Contabilidade (entrada)' },
  6: { color: '#722ed1', short: 'Outros', full: 'Outros' },
  7: { color: '#f5222d', short: 'Troca de Contabilidade', full: 'Troca de Contabilidade (saída)' },
};

const getCategory = (category) => CATEGORIES[parseInt(category)] || null;

export const getBackgroundItem = (category) => {
  const found = getCategory(category);
  return found ? found.color : '#8c8c8c';
}

export const getTextItem = (category) => {
  const found = getCategory(category);
  return found ? found.short : 'Tarefa';
}

export const getCategoryLabel = (category) => {
  const found = getCategory(category);
  return found ? found.full : 'Tarefa';
}

// Uma cobranca so conta como quitada se existir alguma parcela. Sem esta
// verificacao, `[].every(...)` devolve true e uma cobranca vazia era exibida
// como paga — de verde no card e com "Pago" no tooltip.
export const areAllInstallmentsPaid = (payment) => {
  if (!payment || !Array.isArray(payment.installments) || payment.installments.length === 0) {
    return false;
  }
  return payment.installments.every((installment) => installment.status === 'Pago');
}

export const hasInstallmentWithStatus = (payment, status) => {
  if (!payment || !Array.isArray(payment.installments)) {
    return false;
  }
  return payment.installments.some((installment) => installment.status === status);
}

// ---------------------------------------------------------------------------
// Etapas do processo
//
// Substituem o campo "Pendência" (Escritório / Órgão Público / Cliente), que o
// departamento nunca usou. Cada etapa nasce PENDENTE (amarelo) e o executor a
// marca como CONCLUÍDA (verde) ou NÃO APLICÁVEL (vermelho) — nem toda demanda
// passa pela prefeitura ou por conselho de classe.
//
// Ficam gravadas em custom_fields.stages:
//   { documentos: { status: 'done', at: '2026-08-24 10:00:00', by: 12 }, ... }
// O carimbo `at` é feito no servidor, na transição de pendente para marcada.
// ---------------------------------------------------------------------------
export const STAGE_DONE = 'done';
export const STAGE_NA = 'na';

// Etapas de cada tipo de ticket. A chave é a identidade da etapa: onde o passo
// é o mesmo em vários tipos (Receita, Junta, Prefeitura...) ela se repete, e por
// isso um ticket aberto não perde o que foi marcado quando a lista muda.
// `short` é a sigla do card; o nome completo vai no tooltip.
//
// A única fonte das listas é o servidor (SocietarioStagesService): a padrão ou
// a que o escritório montou em Configurações. Chegam por useSocietarioStages;
// até lá não há etapa nenhuma a desenhar — melhor que mostrar uma lista que
// pode não ser a do escritório e trocar na frente do usuário.
let loadedStages = null;

export const setStagesConfig = (categories) => {
  const next = {};

  Object.keys(categories || {}).forEach((category) => {
    const entry = categories[category];
    const list = entry && entry.stages;

    if (Array.isArray(list)) {
      next[parseInt(category)] = list.map((stage) => ({
        key: stage.key,
        short: stage.short,
        label: stage.label,
      }));
    }
  });

  loadedStages = next;
};

export const areStagesLoaded = () => loadedStages !== null;

// `scope` é o tipo do ticket ou, em ticket finalizado, a própria lista com que
// ele foi concluído (custom_fields.stage_list) — ver getStageScope.
export const getStagesFor = (scope) => {
  if (Array.isArray(scope)) return scope;
  return (loadedStages && loadedStages[parseInt(scope)]) || [];
};

// Ticket finalizado guarda a lista em vigor na conclusão: mudar a lista do tipo
// depois não o "reabre" nem mexe no tempo que ele levou.
export const getStageScope = (item) => {
  if (!item) return null;
  const list = item.stage_list || (item.custom_fields && item.custom_fields.stage_list);
  return Array.isArray(list) && list.length > 0 ? list : item.category;
};

const STAGE_KEY_PATTERN = /^[a-z0-9_]{1,40}$/;

// Sem tipo, vale qualquer etapa marcada — inclusive a de uma etapa que saiu da
// lista, que continua guardada no ticket.
const getStageKeys = (category, parsed) => (
  category === undefined || category === null
    ? Object.keys(parsed).filter((key) => STAGE_KEY_PATTERN.test(key))
    : getStagesFor(category).map((stage) => stage.key)
);

export const getStageStatusLabel = (status) => {
  if (status === STAGE_DONE) return 'Concluído';
  if (status === STAGE_NA) return 'Não aplicável';
  return 'Pendente';
};

// Aceita tanto o objeto ja normalizado quanto o JSON cru (o painel devolve
// custom_fields decodificado, mas uma resposta de salvamento pode trazer string).
// Com `category`, devolve só as etapas daquele tipo de ticket.
export const normalizeStages = (stages, category) => {
  let parsed = stages;

  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      parsed = null;
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  const normalized = {};
  getStageKeys(category, parsed).forEach((key) => {
    const value = parsed[key];
    const status = value && value.status;
    if (status === STAGE_DONE || status === STAGE_NA) {
      normalized[key] = {
        status: status,
        at: (value && value.at) || null,
        by: (value && value.by) || null,
      };
      // Data ajustada à mão: guarda quem ajustou.
      if (value && value.edited_by) {
        normalized[key].edited_by = value.edited_by;
      }
    }
  });

  return normalized;
};

export const getTaskStages = (item) => {
  if (!item) return {};
  const scope = getStageScope(item);
  if (item.stages) return normalizeStages(item.stages, scope);
  return normalizeStages(item.custom_fields && item.custom_fields.stages, scope);
};

// Observações das etapas, gravadas à parte das marcações (custom_fields.stage_notes)
// para que uma etapa ainda pendente também possa ter nota.
export const getStageNotes = (source) => {
  const notes = source && (source.stage_notes || (source.custom_fields && source.custom_fields.stage_notes));
  return notes && typeof notes === 'object' && !Array.isArray(notes) ? notes : {};
};

// Último movimento do processo: a etapa marcada mais recente; sem nenhuma,
// quando o ticket entrou em processo ou foi criado. É o que o filtro
// "Parado há mais de X dias" compara.
export const getLastStageActivity = (item) => {
  if (!item) return null;

  const stages = getTaskStages(item);
  const times = Object.keys(stages)
    .map((key) => stages[key].at)
    .concat([item.processed_at, item.created_at])
    .filter((at) => !!at)
    .map((at) => new Date(String(at).replace(' ', 'T')).getTime())
    .filter((time) => !isNaN(time));

  return times.length > 0 ? Math.max.apply(null, times) : null;
};

export const getDaysSinceLastActivity = (item, now) => {
  const last = getLastStageActivity(item);
  if (last === null) return null;
  return Math.max(0, Math.floor(((now || Date.now()) - last) / 86400000));
};

// Todas as etapas em vigor no escritório, de todos os tipos, sem repetir —
// opções do filtro "Etapa da vez" no painel.
export const getStageFilterOptions = () => {
  const seen = {};
  const options = [];

  [1, 2, 3, 5, 7, 4, 6].forEach((category) => {
    getStagesFor(category).forEach((stage) => {
      if (!seen[stage.key]) {
        seen[stage.key] = true;
        options.push({ key: stage.key, short: stage.short, label: stage.label });
      }
    });
  });

  return options;
};

export const countStagesMarked = (stages, category) => Object.keys(normalizeStages(stages, category)).length;

export const areAllStagesMarked = (stages, category) => (
  countStagesMarked(stages, category) === getStagesFor(category).length
);

// Cronometro do processo: conta a partir da PRIMEIRA etapa marcada e congela
// quando todas as etapas do tipo foram resolvidas. E daqui que sai o tempo medio
// por categoria (quanto demora uma abertura, uma alteração) nos relatorios.
export const getStagesTiming = (stages, category) => {
  const normalized = normalizeStages(stages, category);
  const timestamps = Object.keys(normalized)
    .map((key) => normalized[key].at)
    .filter((at) => !!at)
    .map((at) => new Date(String(at).replace(' ', 'T')).getTime())
    .filter((time) => !isNaN(time));

  if (timestamps.length === 0) {
    return null;
  }

  const startedAt = Math.min.apply(null, timestamps);
  const finished = areAllStagesMarked(normalized, category);
  const endedAt = finished ? Math.max.apply(null, timestamps) : Date.now();
  const days = Math.max(0, Math.floor((endedAt - startedAt) / 86400000));

  return { days: days, running: !finished, startedAt: startedAt };
};

// Idade do processo. O departamento raciocina em degraus ("15 dias, 20 dias,
// 30 dias"), entao o cronometro muda de cor conforme envelhece: e o que
// transforma o numero em sinal de alerta em vez de enfeite.
export const STAGE_AGE_WARN_DAYS = 15;
export const STAGE_AGE_LATE_DAYS = 30;

export const getStagesAgeTone = (timing) => {
  if (!timing) return null;
  if (!timing.running) return 'finished';
  if (timing.days >= STAGE_AGE_LATE_DAYS) return 'late';
  if (timing.days >= STAGE_AGE_WARN_DAYS) return 'warn';
  return 'fresh';
};

// Proxima etapa a ser resolvida — so faz sentido depois que o processo comecou.
export const getNextStageKey = (stages, category) => {
  const normalized = normalizeStages(stages, category);
  const list = getStagesFor(category);

  if (Object.keys(normalized).length === 0) {
    return null;
  }

  for (let i = 0; i < list.length; i++) {
    if (!normalized[list[i].key]) {
      return list[i].key;
    }
  }

  return null;
};

// Compara so o que o usuario escolheu (o status), ignorando data e autor, que
// sao carimbados no servidor. Usado para avisar que ha marcacao nao salva.
export const stagesEqual = (a, b, category) => {
  const left = normalizeStages(a, category);
  const right = normalizeStages(b, category);
  const keys = Object.keys(left);

  if (keys.length !== Object.keys(right).length) {
    return false;
  }

  return keys.every((key) => right[key] && right[key].status === left[key].status);
};

// ---------------------------------------------------------------------------
// Estado da cobranca do ticket
//
// Antes essa leitura vivia espalhada pelo card em duas funcoes proprias
// (`getBackgroundCard` e `hasCharge`) mais um par de tooltips, cada uma
// respondendo a uma fatia da mesma pergunta — e o resultado era so uma cor de
// fundo. Aqui sai um estado unico, que o card traduz em faixa lateral, selo e
// texto. A cor deixa de ser a unica portadora do recado.
// ---------------------------------------------------------------------------
export const CHARGE_COURTESY = 'courtesy';
export const CHARGE_PAID = 'paid';
export const CHARGE_OPEN = 'open';
export const CHARGE_OVERDUE = 'overdue';
export const CHARGE_SUED = 'sued';

export const getChargeState = (item) => {
  if (!item) {
    return CHARGE_COURTESY;
  }

  const payment = item.payment;

  // Sem cobranca vinculada o ticket ainda pode ter valor digitado na aba
  // Financeiro; so e cortesia quando nao ha nem um nem outro.
  if (!payment) {
    const amount = item.custom_fields && item.custom_fields.amount;
    return (amount && Number(amount) > 0) ? CHARGE_OPEN : CHARGE_COURTESY;
  }

  if (areAllInstallmentsPaid(payment)) {
    return CHARGE_PAID;
  }
  if (hasInstallmentWithStatus(payment, 'Inadimplente')) {
    return CHARGE_OVERDUE;
  }
  if (hasInstallmentWithStatus(payment, 'Ajuizado')) {
    return CHARGE_SUED;
  }

  return CHARGE_OPEN;
};

// `accent` pinta a faixa lateral do card; `tint` so e diferente de branco nos
// dois estados que pedem atencao, para o quadro nao virar um vitral.
export const CHARGE_TONES = {
  [CHARGE_COURTESY]: {
    accent: '#52c41a',
    tint: '#ffffff',
    label: 'Cortesia',
    color: '#389e0d',
    background: '#f6ffed',
    border: '#b7eb8f',
    title: 'Cortesia: nenhum valor foi definido na aba Financeiro',
  },
  [CHARGE_PAID]: {
    accent: '#52c41a',
    tint: '#ffffff',
    label: 'Cobrança paga',
    color: '#389e0d',
    background: '#f6ffed',
    border: '#b7eb8f',
    title: 'Todas as parcelas estão quitadas',
  },
  [CHARGE_OPEN]: {
    accent: '#d9d9d9',
    tint: '#ffffff',
    label: 'A receber',
    color: '#595959',
    background: '#fafafa',
    border: '#e8e8e8',
    title: 'Cobrança vinculada, com parcelas ainda em aberto',
  },
  [CHARGE_OVERDUE]: {
    accent: '#f5222d',
    tint: '#fff8f7',
    label: 'Inadimplente',
    color: '#cf1322',
    background: '#fff1f0',
    border: '#ffa39e',
    title: 'Há parcela inadimplente nesta cobrança',
  },
  [CHARGE_SUED]: {
    accent: '#fa8c16',
    tint: '#fffdf7',
    label: 'Ajuizado',
    color: '#d46b08',
    background: '#fff7e6',
    border: '#ffd591',
    title: 'Há parcela ajuizada nesta cobrança',
  },
};

export const getChargeTone = (item) => CHARGE_TONES[getChargeState(item)] || CHARGE_TONES[CHARGE_COURTESY];

// As cores das categorias sao fortes demais para preencher uma tarja inteira no
// card. Rebaixadas a fundo e borda translucidos, elas identificam sem gritar.
export const withAlpha = (hex, alpha) => {
  const value = String(hex).replace('#', '');

  if (value.length !== 6) {
    return hex;
  }

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return hex;
  }

  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
};
